import type { ComponentProps, ReactNode } from 'react';

import { type Size, cn } from '@playbook/utils';

import css from './styles.module.css';

export type RadioSize = Extract<Size, 'sm' | 'md'>;

const DefaultRadioSize: RadioSize = 'md' as const;

type RadioEventHandlers = {
	[K in keyof ComponentProps<'input'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'input'>[K];
};

interface RadioDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface RadioProperties extends RadioEventHandlers, RadioDataAttributes {
	size?: RadioSize;
	disabled?: boolean;
	required?: boolean;

	'aria-describedby'?: string;

	/** Shared by every radio in the group — it is what makes them one group. */
	name: string;
	value: string;
	id?: string;
	checked?: boolean;
	defaultChecked?: boolean;

	/** Text beside the circle. Without it the radio is the bare control. */
	label?: string;
	description?: string;
}

/**
 *	Only the label is inside the `<label>`: a label's accessible name is its
 *	whole subtree, so the description sits outside it and is pointed at with
 *	`aria-describedby` instead.
 */

export function Radio({
	size = DefaultRadioSize,
	disabled = false,
	required = false,

	'aria-describedby': describedBy,

	name,
	value,
	id,
	checked,
	defaultChecked,

	label,
	description,

	...props
}: RadioProperties): ReactNode {
	const fieldId: string = id ?? `${name}-${value}`;
	const className: string = cn(css.radio, css[size]);

	const control: ReactNode = (
		<span data-size={size} className={className}>
			<input
				{...props}
				type="radio"
				id={fieldId}
				name={name}
				value={value}
				checked={checked}
				defaultChecked={defaultChecked}
				disabled={disabled}
				required={required}
				aria-describedby={description ? `${fieldId}-description` : describedBy}
				className={css.field}
			/>
			<span aria-hidden="true" className={css.circle} />
		</span>
	);

	if (!label) {
		return control;
	}

	return (
		<div data-size={size} className={cn(css.option, css[size])}>
			{control}
			<span className={css.text}>
				<label htmlFor={fieldId} className={css.label}>
					{label}
				</label>
				{description && (
					<span id={`${fieldId}-description`} className={css.description}>
						{description}
					</span>
				)}
			</span>
		</div>
	);
}
